/**
 * usePricingRuleCoupons — the coupon code(s) linked to a given Pricing Rule.
 *
 * A Coupon Code doc points at its Pricing Rule via the `pricing_rule` link field,
 * so we query that relationship straight from the doctype (tenant-isolated
 * `getERPClient().list`, not a new endpoint). Used by the Trigger card to let the
 * owner jump to the exact coupon this rule drives instead of the whole list.
 */

import { useQuery } from "@tanstack/react-query";
import { getERPClient } from "@/lib/erp/client";

export interface LinkedCoupon {
  name: string;
  coupon_code: string;
  used?: number;
  maximum_use?: number;
  current_collections?: number;
  max_collections?: number;
  coupon_type?: string;
}

export function usePricingRuleCoupons(pricingRuleName: string, enabled: boolean) {
  return useQuery({
    queryKey: ["pricing-rule-coupons", pricingRuleName],
    enabled: enabled && !!pricingRuleName,
    staleTime: 60_000,
    queryFn: () =>
      getERPClient().list<LinkedCoupon>({
        doctype: "Coupon Code",
        filters: { pricing_rule: pricingRuleName },
        fields: [
          "name",
          "coupon_code",
          "used",
          "maximum_use",
          "current_collections",
          "max_collections",
          "coupon_type",
        ],
        limit_page_length: 20,
      }),
  });
}

/**
 * The real Item docs a Pricing Rule targets (apply_on === "Item Code"), so the
 * "Applies to" row can show the human product name + thumbnail instead of a raw
 * warehouse code like `CS-BDL-KRM-W`. Same tenant-isolated `getERPClient().list`
 * relationship query as above — `item.image` is a direct URL, rendered exactly
 * like ProductListView / BundleComponentsSection.
 */
export interface RuleTargetItem {
  name: string;
  item_code: string;
  item_name?: string;
  image?: string;
}

export function usePricingRuleItems(itemCodes: string[], enabled: boolean) {
  return useQuery({
    queryKey: ["pricing-rule-items", [...itemCodes].sort()],
    enabled: enabled && itemCodes.length > 0,
    staleTime: 60_000,
    queryFn: () =>
      getERPClient().list<RuleTargetItem>({
        doctype: "Item",
        filters: { item_code: ["in", itemCodes] },
        fields: ["name", "item_code", "item_name", "image"],
        limit_page_length: 50,
      }),
  });
}
